import reduce from 'lodash/reduce';
import split from 'lodash/split';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import union from 'lodash/union';
import sortedUniq from 'lodash/sortedUniq';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import chunk from 'lodash/chunk';
import sortBy from 'lodash/sortBy';
import isString from 'lodash/isString';
import deburr from 'lodash/deburr';
import kebabCase from 'lodash/kebabCase';
import {arrayToParams} from "@/grandus-lib/utils/filter";

const CATEGORY_URI_PART = 'kategoria';
const SEARCH_URI_PART = 'vyhladavanie';

export const manageUrlWithParams = (
  name,
  value,
  searchParams,
  isDelete = false,
  urlPrefix = '',
) => {
  const params = new URLSearchParams(searchParams);
  if (isDelete) {
    params.delete(name, value);
  } else {
    params.append(name, value);
  }

  const uri = params.toString();

  return urlPrefix + (uri ? '?' + uri : '');
};

export const propsToParams = props => {
  const params = {
    locale: props?.params?.locale,
  };

  if (props?.params?.term) {
    params.search = props.params.term;
  }

  if (props?.params?.category) {
    params.category = props.params.category;
  }

  if (props?.params?.campaign) {
    params.marketingCampaign = props.params.campaign;
  }

  const parameters = props?.params?.parameters ?? [];

  params.parameters = reduce(
    parameters,
    (carry, param, index) => {
      if (index % 2 === 0) {
        carry[param] = split(
          decodeURIComponent(parameters[index + 1] ?? ''),
          ',',
        );
      }

      return carry;
    },
    {},
  );

  return params;
};

export const getFilterUrl = (
  filterParams,
  parameterKey,
  parameterValue,
  isRemove = false,
  isReplace = false,
  sortValues = true,
  isReset = false,
) => {
  const category = filterParams?.category ?? null;
  const search = filterParams?.search ?? null;
  let parameters = filterParams?.parameters ?? {};
  parameters = isArray(parameters) ? arrayToParams(parameters) : parameters;
  const selected = isReset ? {} : parameters ?? {};

  let linkAttributesFnName = getCategoryLinkAttributes;
  if (!category && search) {
    linkAttributesFnName = getSearchLinkAttributes;
  }

  const urlSegments = linkAttributesFnName.apply(this,
    [
        category ?? search,
        paramsToPath(
          isRemove
            ? removeParameter(selected, parameterKey, parameterValue)
            : addParameter(
                selected,
                parameterKey,
                parameterValue,
                isReplace,
                sortValues,
              ),
        ),
        parameters, //query
        { toDelete: ['category', 'parameters', 'page', 'term'] },
      ]
  );

  return get(urlSegments, 'as', '');
};

export const addParameter = (
  parameters,
  parameterKey,
  parameterValues,
  replace = true,
  sort = true,
) => {
  if (!isObject(parameters)) {
    return {};
  }

  const parametersActual = { ...parameters };

  if (!parameterKey || isEmpty(parameterValues) || !parameterValues) {
    return parametersActual;
  }

  const parameterValuesActual = isArray(parameterValues)
    ? parameterValues
    : [parameterValues];

  if (replace) {
    parametersActual[parameterKey] = parameterValuesActual;
  } else {
    parametersActual[parameterKey] = union(
        parametersActual[parameterKey],
        parameterValuesActual,
    );
  }

  if (sort) {
    parametersActual[parameterKey] = sortedUniq(
      parametersActual[parameterKey].sort(),
    );
  } else {
    parametersActual[parameterKey] = uniq(parametersActual[parameterKey]);
  }

  return { ...parametersActual };
};

export const removeParameter = (
  parameters,
  parameterKey,
  parameterValue = false,
) => {
  if (isEmpty(parameters)) {
    return {};
  }

  if (!parameterKey) {
    return {};
  }

  const parametersActual = { ...parameters };

  if (parameterValue) {
    const removed = without(
      parametersActual[parameterKey],
      encodeURIComponent(parameterValue),
    );

    if (isEmpty(removed)) {
      delete parametersActual[parameterKey];
    } else {
      parametersActual[parameterKey] = sortedUniq(removed);
    }
  } else {
    delete parametersActual[parameterKey];
  }

  return { ...parametersActual };
};

export const paramsToPath = params => {
  return arrayToPath(flatMap(params, (value, key) => [key, value]));
};

export const arrayToPath = array => {
  if (isEmpty(array)) {
    return '';
  }
  return flatten(sortChunks(chunk(array, 2))).join('/');
};

const sortChunks = chunks => sortBy(chunks, pair => pair[0]);

export const getCategoryLinkAttributes = (
  category,
  parameters = '',
  query = {},
  options = {},
) => {
  if (get(options, 'absoluteHref')) {
    return { href: options.absoluteHref };
  }

  const emptyResult = {
    href: {
      pathname: `/`,
      query: {},
    },
    as: {
      pathname: `/`,
      query: {},
    },
  };

  if (!category || !isString(category)) {
    return emptyResult;
  }

  const newQuery = get(options, 'toDelete')
    ? queryToQuery(
        query,
        get(options, 'dataToChange', {}),
        get(options, 'toDelete'),
      )
    : queryToQuery(query, get(options, 'dataToChange', {}));

  return {
    href: {
      pathname: `/${CATEGORY_URI_PART}/[category]/[[...parameters]]`,
      query: newQuery,
    },
    as: {
      pathname: `/${CATEGORY_URI_PART}/${category}/${parameters}`,
      query: newQuery,
    },
  };
};

export const getSearchLinkAttributes = (
    term,
    parameters = '',
    query = {},
    options = {},
) => {
  if (get(options, 'absoluteHref')) {
    return { href: options.absoluteHref };
  }

  const emptyResult = {
    href: {
      pathname: `/`,
      query: {},
    },
    as: {
      pathname: `/`,
      query: {},
    },
  };

  if (!term || !isString(term)) {
    return emptyResult;
  }

  const newQuery = get(options, 'toDelete')
      ? queryToQuery(
          query,
          get(options, 'dataToChange', {}),
          get(options, 'toDelete'),
      )
      : queryToQuery(query, get(options, 'dataToChange', {}));

  return {
    href: {
      pathname: `/${SEARCH_URI_PART}/[term]/[[...parameters]]`,
      query: newQuery,
    },
    as: {
      pathname: `/${SEARCH_URI_PART}/${term}/${parameters}`,
      query: newQuery,
    },
  };
};

export const queryToQuery = (
  query,
  dataToChange = {},
  toDelete = ['parameters', 'category'],
) => {
  let newQuery = {
    ...query,
    ...dataToChange,
  };

  if (!isEmpty(toDelete)) {
    toDelete.map(key => delete newQuery[key]);
  }

  return newQuery;
};

export const parseInput = str => {
  return kebabCase(deburr(str));
};
